// HeaderSearch — the field (boards 17 and 18). The panel under it is `SearchPanel.module.scss`, the three dresses of
// a token are `SearchTokens.module.scss`; this file lays the field out and, on the sheet surface, the full screen
// and half sheet the field stands in while the panel is open.

.slot {
    position: relative;
    flex: 1;
    max-width: 460px;
    min-width: 0;
    display: flex;
    align-items: center;
}

// `display: contents` on the desktop: the shell is the sheet's box only on the sheet surface.
.shell {
    display: contents;
}

.top {
    display: contents;
}

.field {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 7px;
    box-sizing: border-box;
    min-height: 40px;
    background: var(--tl-surface);
    border: 1px solid var(--tl-border-strong);
    border-radius: var(--tl-radius-chrome-lg);
    padding: 8px 13px;
    color: var(--tl-dim);
}

// Focused or open: the border is the accent as a line.
.slot:focus-within .field,
.slot[data-open] .field {
    border-color: var(--tl-accent-line);
}

// The landing ring: a phrase tapped on a tile lands as a pill and the border rings once. Two names alternate so a
// second landing restarts it.
.ringEven {
    animation: ring-even 800ms ease-out;
}

.ringOdd {
    animation: ring-odd 800ms ease-out;
}

@keyframes ring-even {
    from {
        border-color: var(--tl-accent);
        box-shadow: 0 0 0 4px var(--tl-halo);
    }
}

@keyframes ring-odd {
    from {
        border-color: var(--tl-accent);
        box-shadow: 0 0 0 4px var(--tl-halo);
    }
}

@media (prefers-reduced-motion: reduce) {
    .ringEven,
    .ringOdd {
        animation: none;
    }
}

// The tokens and the input share one sideways-scrolling track: a few tokens never push the field wider, they scroll
// with the words. An edge fades only where there is more to scroll to (`trackEdges`).
.track {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 6px;
    // room for a marked token's ring, which the scrolling track would otherwise clip
    padding: 3px 2px;
    margin: -3px -2px;
    overflow-x: auto;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
}

.track[data-fade-left] {
    -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 8px);
    mask-image: linear-gradient(90deg, transparent 0, #000 8px);
}

.track[data-fade-right] {
    -webkit-mask-image: linear-gradient(90deg, #000 calc(100% - 12px), transparent 100%);
    mask-image: linear-gradient(90deg, #000 calc(100% - 12px), transparent 100%);
}

.track[data-fade-left][data-fade-right] {
    -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 8px, #000 calc(100% - 12px), transparent 100%);
    mask-image: linear-gradient(90deg, transparent 0, #000 8px, #000 calc(100% - 12px), transparent 100%);
}

.leanGroup {
    display: contents;
}

// `FOR YOU`: the lean's name, at rest (in the field on a desktop, on the header's second line on a phone).
.forYou,
.lineLabel {
    flex: none;
    font-family: var(--tl-font-label);
    font-size: 9px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--tl-dim);
}

// The input and the words it is answered by: the question when nothing stands in the field, `and…` once something
// does (the field is never closed), and never beside typed text.
.inputWrap {
    position: relative;
    display: flex;
    flex: 1 0 70px;
    min-width: 70px;
}

.input {
    flex: 1;
    min-width: 0;
    background: none;
    border: none;
    outline: none;
    caret-color: var(--tl-accent); // the tungsten caret, the field's own (N15)
    font-family: var(--tl-font);
    font-size: 13.5px;
    color: var(--tl-text);

    &::placeholder {
        color: transparent;
    }
}

.hintAnd,
.hintQuestion {
    display: none;
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    pointer-events: none;
    font-size: 13.5px;
    color: var(--tl-dim);
}

.inputWrap[data-tokens] .hintAnd,
.inputWrap:not([data-tokens]) .hintQuestion {
    display: block;
}

.input:not(:placeholder-shown) ~ .hintAnd,
.input:not(:placeholder-shown) ~ .hintQuestion {
    display: none;
}

// Above the fold threshold the field is open at rest, so neither the verb nor the way back out of
// it exists: `display: none` also keeps both out of the tab order.
.glyph,
.close,
.back,
.grab,
.leanLine,
.scrim {
    display: none;
}

// The clearing × (plan 2026_09_04_wall_header_reveal.md § Step 2) — unlike `.glyph`/`.close`, NOT
// gated behind the mobile media query below: it is a field-at-rest affordance, and the field is at
// rest at every width above 640px regardless of `phone`, and below it too under `phone: 'open'`.
// Sized and positioned like `.close` (44x44 tap target, 13x13 visible glyph, flush at the field's
// own right edge) but tuned for THIS rule's own field padding rather than the phone field's.
.clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 44px;
    height: 44px;
    margin: -14px -15px -14px 0; // centre 20px in, mirroring the 14px magnifier at padding 13
    padding: 0;
    border: none;
    border-radius: 50%;
    background: none;
    color: var(--tl-muted);
}

// The line "wall art dropped · Undo" under the field.
.undo {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border: 1px solid var(--tl-border-strong);
    border-radius: var(--tl-radius-pill);
    background: var(--tl-surface);
    font-size: 12.5px;
    white-space: nowrap;

    // The house's base `p` rule pads the top (Toile: 6.25px), which sat the line below its Undo.
    p {
        margin: 0;
        padding: 0;
        line-height: inherit;
    }
}

.undoAction {
    padding: 0;
    border: none;
    background: none;
    font: inherit;
    color: var(--tl-accent);
    cursor: pointer;
}

// A notice under the field pushes the open dropdown down by its own height.
.slot[data-notice] [data-layout='dropdown'] {
    top: calc(100% + 40px);
}

// Keyed on `[data-wall-surface="sheet"]` (`Wall.tsx`'s `isSheetSurface()`, published on `<html>`),
// not a width breakpoint (code-quality review F4 / least-and-simplest review §2.3): the surface
// decision already answers "is this the phone shapes or the desktop ones" — a touch tablet in
// landscape gets the Sheet by the same rule and needs the same shapes a phone does, so this must
// track that decision rather than restate its own 640px guess at it.
:global(html[data-wall-surface='sheet']) {
    // The iOS focus-zoom fix (plan § Step 2) applies to the input at this width in EITHER mode.
    .input {
        font-size: 16px;
    }

    .hintAnd,
    .hintQuestion {
        font-size: 16px;
    }

    .field {
        padding: 7px 12px;
        min-height: 44px;
    }

    .clear {
        margin: -14px -15px -14px 0; // centre 19px in, mirroring the magnifier at padding 12
    }

    // At rest the phone field holds the question beside the wordmark, and the lean stands on its own line in the
    // header (`.leanLine`); the lean enters the field while it is open.
    .slot:not([data-open]) .leanGroup,
    .forYou {
        display: none;
    }

    .slot:not([data-open]) .inputWrap[data-tokens]:not([data-yours]) .hintAnd {
        display: none;
    }

    .slot:not([data-open]) .inputWrap[data-tokens]:not([data-yours]) .hintQuestion {
        display: block;
    }

    .input:not(:placeholder-shown) ~ .hintQuestion {
        display: none;
    }

    // The header's second line: `FOR YOU` and the lean, inside the header's ground, hiding and returning with it.
    .leanLine {
        display: flex;
        flex: 0 0 100%;
        order: 10;
        align-items: center;
        gap: 6px;
        padding-top: 8px;
        overflow-x: auto;
        scrollbar-width: none;
    }

    .leanLine[data-hidden] {
        display: none;
    }

    // The panel open: the field stands in a sheet over the whole screen (full) or hanging from the top (half).
    .slot[data-open] {
        max-width: none;
    }

    .slot[data-open] .shell {
        position: fixed;
        inset: 0;
        height: 100dvh;
        z-index: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
        background: var(--tl-stage);
    }

    .slot[data-open] .shell[data-layout='half'] {
        bottom: auto;
        height: auto;
        max-height: 100dvh;
        border-radius: 0 0 var(--tl-radius-container-lg) var(--tl-radius-container-lg);
        border-bottom: 1px solid var(--tl-border-strong);
        background: var(--tl-surface);
        box-shadow: 0 18px 40px color-mix(in srgb, var(--tl-shadow-ink) 50%, transparent);
    }

    .slot[data-open] .top {
        display: flex;
        align-items: center;
        gap: 10px;
        flex: none;
        padding: 14px 14px 10px;
        border-bottom: 1px solid var(--tl-border);
    }

    .shell[data-layout='half'] .top {
        padding-bottom: 2px;
        border-bottom: none;
        background: none;
    }

    .slot[data-open] .back {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: none;
        width: 28px;
        height: 44px;
        padding: 0;
        border: none;
        background: none;
        color: var(--tl-muted);
        cursor: pointer;
    }

    .slot[data-open] .field > svg {
        display: none;
    }

    // A notice under the field stands in the sheet's flow.
    .slot[data-open] .undo {
        position: static;
        align-self: flex-start;
        margin: 8px 16px 0;
    }

    .slot[data-open] .scrim {
        display: block;
        position: fixed;
        inset: 0;
    }

    .shell[data-layout='half'] .grab {
        display: block;
        width: 100%;
        padding: 4px 0 10px;
        border: none;
        background: none;
        cursor: grab;
        touch-action: none;
    }

    .shell[data-layout='half'] .grab::before {
        content: '';
        display: block;
        width: 36px;
        height: 4px;
        margin: 0 auto;
        border-radius: var(--tl-radius-pill);
        background: var(--tl-border-strong);
    }

    // The top phrases above the keyboard, as offered chips; pinned by the shell's own size (`useVisualViewport`).
    .strip {
        display: flex;
        flex: none;
        flex-wrap: wrap;
        gap: 6px;
        padding: 8px 10px;
        border-top: 1px solid var(--tl-border);
        background: var(--tl-surface);
    }

    // The fold — `phone: 'fold'` only (no caller opts into it today; a house that does still gets the shipped shape,
    // edge case 24). `phone: 'open'` skips every rule below.
    .slotFold {
        flex: 0 0 auto;
        max-width: none;
    }

    .slotFold .field {
        display: none;
    }

    .slotFold .glyph {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        position: relative;
        // 44x44 CSS px (item 7, wall plan d carried items) — the visible icon (`SearchGlyph`) is
        // unchanged; the margin grows by the same amount the box did, so the tap target grows into
        // the header's gap, not the header's height, at the same footprint as before.
        width: 44px;
        height: 44px;
        margin: -10px -13px;
        padding: 0;
        border: none;
        border-radius: var(--tl-radius-chrome);
        background: none;
        color: var(--tl-muted);
    }

    // A live query with the field folded away: the glyph lights and takes the tungsten dot, so the
    // re-ordered wall under it is explained rather than unaccountable.
    .glyphLit {
        color: var(--tl-text);
    }

    .glyphLit::after {
        content: '';
        position: absolute;
        top: 6px;
        right: 6px;
        width: 5px;
        height: 5px;
        border-radius: 50%;
        background: var(--tl-accent);
    }

    // Unfolded, the field is the header row: the shell stands its wordmark and nav down (Toile's
    // `.headerSearching`) and this takes every pixel they leave.
    .slotOpen {
        flex: 1;
        min-width: 0;
    }

    .slotOpen .glyph {
        display: none;
    }

    .slotOpen .field {
        display: flex;
    }

    .slotOpen .close {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: none;
        // 44x44 CSS px (item 7): same net footprint as the shipped 22x22/-3px -4px -3px 0 — the
        // visible × (13x13 svg) and the field's own width are unchanged, only the invisible tap
        // area grows, flush against the input on the left as it always was.
        width: 44px;
        height: 44px;
        margin: -14px -26px -14px 0;
        padding: 0;
        border: none;
        border-radius: 50%;
        background: none;
        color: var(--tl-muted);
    }
}
